import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { restManager } from 'app/ajs-upgraded-providers';
import { BarcodeManagerService, ConfigurationManagerService, EntityManagerService } from 'src/app/core';
import { MathUtils } from 'src/app/shared/utils';
import { Items, RawMaterials, Suppliers } from 'tilby-models';
import {
  MovementEntry,
  NewMovementRowValue,
  StockLocationOption,
  SUPPLIERS_ORDERS_LOCATION_ID,
  SALES_LOCATION_ID
} from '../models/stock-new-movement.model';

// ultimo id dei magazzini di sistema, il cui nome va tradotto
const SYSTEM_STOCK_LOCATIONS_MAX_ID = 100;

type LocationStock = Map<string, any>;

const toFinite = (value: any): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};


@Injectable()
export class StockNewMovementDataService {

  private readonly entityManager = inject(EntityManagerService);
  private readonly restManagerService = inject(restManager);
  private readonly translateService = inject(TranslateService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly barcodeManagerService = inject(BarcodeManagerService);

  readonly ready = signal(false);
  readonly stockLocations = signal<StockLocationOption[]>([]);

  // prodotti (una voce per combinazione) e materie prime selezionabili
  entries: MovementEntry[] = [];
  suppliers: Suppliers[] = [];

  private itemsById = new Map<number, Items>();
  private itemEntries = new Map<string, MovementEntry>();
  private materialEntries = new Map<number, MovementEntry>();
  private suppliersById = new Map<number, Suppliers>();
  private stocksByLocation: Record<string, LocationStock> = {};

  readonly showPriceData = this.configurationManagerService.isModuleEnabled('items');

  async load() {
    const [items, suppliers, rawMaterials, stockCollection, stockLocations] = await Promise.all([
      this.entityManager.items.fetchCollectionOffline() as Promise<Items[]>,
      this.entityManager.suppliers.fetchCollectionOffline() as Promise<Suppliers[]>,
      this.entityManager.rawMaterials.fetchCollectionOffline() as Promise<RawMaterials[]>,
      this.entityManager.stock.fetchCollectionOnline({ stock_location_id: 'notnull' }) as Promise<any[]>,
      this.restManagerService.getList('stock_locations') as Promise<StockLocationOption[]>
    ]);

    this.stockLocations.set(stockLocations.map(location => ({
      ...location,
      name: location.id <= SYSTEM_STOCK_LOCATIONS_MAX_ID
        ? this.translateService.instant(`STOCK.LOCATIONS.${location.name}`)
        : location.name
    })));

    this.buildSuppliers(suppliers);
    this.buildEntries(items, rawMaterials);
    this.buildStocks(stockCollection);

    this.ready.set(true);
  }

  private buildSuppliers(suppliers: Suppliers[]) {
    this.suppliers = suppliers;
    this.suppliersById = new Map(suppliers.map(supplier => [supplier.id!, supplier]));
  }

  private buildEntries(items: Items[], rawMaterials: RawMaterials[]) {
    this.itemsById = new Map(items.map(item => [item.id!, item]));
    this.itemEntries.clear();
    this.materialEntries.clear();

    const sortByName = (a: { name?: string | null }, b: { name?: string | null }) => (a.name ?? '').localeCompare(b.name ?? '');

    // solo prodotti con magazzino semplice o con distinta a produzione manuale
    const itemEntries = items
      .filter(item => item.stock_type === 'simple' || (item.stock_type === 'bom' && (item as any).manual_production))
      .sort(sortByName)
      .flatMap((item): MovementEntry[] => {
        if (item.combinations?.length) {
          return item.combinations.map(combination => ({
            ...item,
            combination_id: combination.id,
            _entryType: 'item' as const,
            _name: `${item.name} (${(combination.combination_values ?? []).map((v: any) => v.value).join(', ')})`
          } as MovementEntry));
        }

        return [{ ...item, _entryType: 'item' as const, _name: item.name } as MovementEntry];
      });

    itemEntries.forEach(entry => this.itemEntries.set(this.getItemKey(entry), entry));

    const materialEntries = [...rawMaterials].sort(sortByName).map(rawMaterial => ({
      ...rawMaterial,
      _entryType: 'raw_material' as const,
      _name: `${rawMaterial.name} (${this.translateService.instant('STOCK.NEW_MOVEMENT.RAW_MATERIAL')})`
    } as MovementEntry));

    materialEntries.forEach(entry => this.materialEntries.set(entry.id, entry));

    this.entries = [...materialEntries, ...itemEntries];
  }

  // giacenze per magazzino, indicizzate per combinazione, prodotto o materia prima
  private buildStocks(stockCollection: any[]) {
    this.stocksByLocation = {};

    for (const stock of stockCollection) {
      const locationStock = (this.stocksByLocation[stock.stock_location_id] ??= new Map());

      if (stock.combination_id) {
        locationStock.set(`combination_id_${stock.combination_id}`, stock);
      } else if (stock.item_id) {
        locationStock.set(`item_id_${stock.item_id}`, stock);
      } else if (stock.raw_material_id) {
        locationStock.set(`raw_material_id_${stock.raw_material_id}`, stock);
      }
    }
  }

  // ------------------- ricerca -------------------

  getItemKey(row: { combination_id?: number | null; id?: number; item_id?: number }): string {
    return row.combination_id
      ? `combination_${row.combination_id}`
      : `item_${row.item_id ?? row.id}`;
  }

  getItemEntry(row: { combination_id?: number | null; item_id?: number; id?: number }): MovementEntry | undefined {
    return this.itemEntries.get(this.getItemKey(row));
  }

  getMaterialEntry(id: number): MovementEntry | undefined {
    return this.materialEntries.get(id);
  }

  getSupplier(id?: number | null): Suppliers | null {
    return (id != null && this.suppliersById.get(id)) || null;
  }

  getOriginalItem(id: number): Items | undefined {
    return this.itemsById.get(id);
  }

  // prodotto o materia prima a cui si riferisce un movimento già esistente (rollback, copia, ricevimento merce)
  getEntryForMovement(movement: any): MovementEntry | null {
    const entry = movement.entry_type === 'item'
      ? this.getItemEntry({ combination_id: movement.combination_id, item_id: movement.item_id })
      : this.getMaterialEntry(movement.raw_material_id);

    return entry ?? null;
  }

  // l'elenco può essere molto lungo: si mostrano solo i primi risultati
  filterEntries(search: string | null | undefined, limit = 100): MovementEntry[] {
    const text = (search ?? '').trim().toLowerCase();

    if (!text) {
      return this.entries.slice(0, limit);
    }

    return this.entries
      .filter(entry => [entry._name, entry['sku'], entry['code']].some(field => String(field ?? '').toLowerCase().includes(text)))
      .slice(0, limit);
  }

  getEntriesAround(selected: MovementEntry, limit = 100): MovementEntry[] {
    const index = this.entries.findIndex(entry =>
      entry._entryType === selected._entryType
      && entry.id === selected.id
      && (entry.combination_id ?? null) === (selected.combination_id ?? null)
    );

    const start = index >= limit ? index - 1 : 0;

    return this.entries.slice(start, start + limit);
  }

  filterSuppliers(search: string | null | undefined): Suppliers[] {
    const text = (search ?? '').trim().toLowerCase();

    return text
      ? this.suppliers.filter(supplier => String(supplier.name ?? '').toLowerCase().includes(text))
      : this.suppliers;
  }

  async searchBarcode(barcode: string) {
    return this.barcodeManagerService.searchBarcodeItem(barcode);
  }

  openCameraBarcode() {
    return this.barcodeManagerService.openCameraBarcode();
  }

  // ------------------- calcoli -------------------

  getRowAmount(row: Pick<NewMovementRowValue, 'price' | 'quantity' | 'perc_discount'>): number {
    return MathUtils.round(toFinite(row.price) * toFinite(row.quantity) * (1 - (toFinite(row.perc_discount) / 100)));
  }

  private getStockKey(entry: MovementEntry): string {
    return entry._entryType === 'raw_material'
      ? `raw_material_id_${entry.id}`
      : (entry.combination_id ? `combination_id_${entry.combination_id}` : `item_id_${entry.id}`);
  }

  // giacenza nel magazzino di vendita più quella degli ordini fornitori
  getSalesAndOrdersQuantity(entry: MovementEntry): number {
    const key = this.getStockKey(entry);

    return toFinite(this.stocksByLocation[SALES_LOCATION_ID]?.get(key)?.stock_quantity)
      + toFinite(this.stocksByLocation[SUPPLIERS_ORDERS_LOCATION_ID]?.get(key)?.stock_quantity);
  }

  // giacenza mostrata accanto alla quantità: (quantità attesa) nel ricevimento merce, altrimenti quella del magazzino
  getCurrentStockQuantity(row: NewMovementRowValue, sourceLocationId: number | null): string | null {
    if (row.oldQuantity) {
      return `(${row.oldQuantity})`;
    }

    const entry = row.entry;

    if (!entry || typeof entry !== 'object') {
      return null;
    }

    const key = this.getStockKey(entry);

    if (sourceLocationId === SUPPLIERS_ORDERS_LOCATION_ID) {
      const salesQuantity = toFinite(this.stocksByLocation[SALES_LOCATION_ID]?.get(key)?.stock_quantity);
      const ordersQuantity = toFinite(this.stocksByLocation[SUPPLIERS_ORDERS_LOCATION_ID]?.get(key)?.stock_quantity);

      return `(${salesQuantity} ${ordersQuantity >= 0 ? '+' : '-'} ${Math.abs(ordersQuantity)})`;
    }

    return `(${toFinite(this.stocksByLocation[sourceLocationId ?? SALES_LOCATION_ID]?.get(key)?.stock_quantity)})`;
  }
}
